import type { ApprovalStatus, ApproverOption } from './api';

const nameOf = (person: ApproverOption | null, fallback: string) => person?.display_name || person?.username || fallback;

/** Shared copy for web and Mini App. Permission and turn remain server-owned. */
export function architectureReviewView(status?: ApprovalStatus | null, stage: 'architecture' | 'estimate' | 'pricing' | 'proposal' = 'architecture') {
  const review = status?.stage_review || status?.architecture_review;
  const title = stage[0].toUpperCase() + stage.slice(1);
  if (!review) return null;
  const reviewer = nameOf(review.general_reviewer, 'General reviewer');
  const author = nameOf(review.author, 'the author');
  const selfReview = !!review.author && review.author.id === review.general_reviewer?.id;
  if (review.phase === 'unavailable') return {
    title: 'Review needs attention', badge: 'Review unavailable', action: `Approve ${stage}`,
    hint: 'The author or General reviewer is unavailable. Ask Root to check the review settings.',
    submitted: '',
  };
  if (review.phase === 'approved') return {
    title: `${title} approved`, badge: 'Approved', action: `Approve ${stage}`, hint: '', submitted: '',
  };
  if (review.phase === 'general') return {
    title: 'Awaiting final approval', badge: 'In review', action: 'Give final approval',
    hint: `Author confirmed · waiting for ${reviewer} (General reviewer)`,
    submitted: `Sent to ${reviewer} for final approval.`,
  };
  return {
    title: selfReview ? 'Ready for final approval' : 'Awaiting author confirmation', badge: 'Draft',
    action: selfReview ? `Approve ${stage}` : 'Confirm & send for review',
    hint: selfReview ? `One approval from ${reviewer}, the author and General reviewer, completes this step.`
      : review.author ? `${author} confirms first, then ${reviewer} gives final approval.`
        : `Confirm as the author to send this ${stage} to ${reviewer}.`,
    submitted: `Sent to ${reviewer} for final approval.`,
  };
}

/** Send the version actually displayed, not a newer version from a status poll. */
export function architectureReviewVersion(status: ApprovalStatus | null | undefined, displayedVersionId?: string) {
  return status?.architecture_review
    ? { expected_architecture_version_id: displayedVersionId ?? null }
    : {};
}

export function architectureReviewIsStale(status: ApprovalStatus | null | undefined, displayedVersionId?: string) {
  return !!status?.architecture_review && status.architecture_review.version_id !== (displayedVersionId ?? null);
}
